import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, List, Plus, Trash2, RotateCcw } from 'lucide-react';

export default function ParamsPanel({ selectedNodes = [], nodeDefs = [], setNodesDirty }) {
  const selectedCount = selectedNodes.length;
  const node = selectedCount === 1 ? selectedNodes[0] : null;
  const nodeType = node?.data?.type || node?.data?.label || '';
  const def = node ? nodeDefs.find((d) => d?.name === nodeType) : null;
  const nodeId = node?.id || null;
  const nodeParams = node?.data?.params;
  const nodeOutputName = node?.data?.output_name;

  const [rows, setRows] = useState([]);
  const [outputName, setOutputName] = useState('');
  const [globalError, setGlobalError] = useState(null);

  const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  useEffect(() => {
    if (!nodeId) return;

    const rawParams = nodeParams || {};
    const legacyOut = typeof rawParams.output_name === 'string' ? rawParams.output_name : ''
    const initialOutputName = typeof nodeOutputName === 'string' ? nodeOutputName : legacyOut
    setOutputName(initialOutputName)

    const { output_name: _ignored, ...initialParams } = rawParams;
    const newRows = Object.entries(initialParams).map(([key, value]) => {
      let type = 'string';
      if (typeof value === 'number') type = 'number';
      else if (typeof value === 'boolean') type = 'boolean';
      else if (typeof value === 'object') type = 'json';

      return {
        id: generateId(),
        key,
        type,
        value: type === 'json' ? JSON.stringify(value) : value,
        error: null
      };
    });
    
    setRows(newRows);
    setGlobalError(null);
  }, [nodeId, nodeParams, nodeOutputName]);

  const handleAddRow = () => {
    setRows(prev => [
      ...prev,
      {
        id: generateId(),
        key: '',
        type: 'string',
        value: '',
        error: null
      }
    ]);
  };

  const handleRemoveRow = (id) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const handleRowChange = (id, field, newValue) => {
    setRows(prev => prev.map(row => {
      if (row.id !== id) return row;
      
      const updatedRow = { ...row, [field]: newValue };
      
      if (field === 'type') {
        if (newValue === 'boolean') updatedRow.value = false;
        else if (newValue === 'number') updatedRow.value = 0;
        else if (newValue === 'json') updatedRow.value = '{}';
        else updatedRow.value = '';
        updatedRow.error = null;
      }

      if (updatedRow.type === 'json' && field === 'value') {
        try {
          JSON.parse(newValue);
          updatedRow.error = null;
        } catch (e) {
          updatedRow.error = 'Invalid JSON'
        }
      }

      return updatedRow;
    }));
  };

  const handleReset = () => {
    if (!nodeId) return;

    const rawParams = nodeParams || {};
    const legacyOut = typeof rawParams.output_name === 'string' ? rawParams.output_name : ''
    const initialOutputName = typeof nodeOutputName === 'string' ? nodeOutputName : legacyOut
    setOutputName(initialOutputName)

    const { output_name: _ignored, ...initialParams } = rawParams;
    const newRows = Object.entries(initialParams).map(([key, value]) => {
        let type = 'string';
        if (typeof value === 'number') type = 'number';
        else if (typeof value === 'boolean') type = 'boolean';
        else if (typeof value === 'object') type = 'json';
  
        return {
          id: generateId(),
          key,
          type,
          value: type === 'json' ? JSON.stringify(value) : value,
          error: null
        };
    });
    setRows(newRows);
    setGlobalError(null);
  }

  const handleApply = () => {
    const keys = rows.map(r => r.key.trim());
    const uniqueKeys = new Set(keys);

    if (keys.includes('output_name')) {
      setGlobalError("Use the 'Output Name' field instead of defining an output_name param.");
      return;
    }
    
    if (keys.some(k => !k)) {
      setGlobalError("All parameter keys must be non-empty.");
      return;
    }
    
    if (keys.length !== uniqueKeys.size) {
      setGlobalError("All parameter keys must be unique.");
      return;
    }

    const newParams = {};
    let hasError = false;

    const validatedRows = rows.map(row => {
      const key = row.key.trim();
      let val = row.value;

      if (row.type === 'number') {
        val = Number(row.value);
      } else if (row.type === 'json') {
        try {
          val = JSON.parse(row.value);
        } catch (e) {
            hasError = true;
            return { ...row, error: "Invalid JSON" };
        }
      }
      
      newParams[key] = val;
      return { ...row, error: null };
    });

    if (hasError) {
      setRows(validatedRows);
      setGlobalError("Please fix errors before applying.");
      return;
    }

    const finalOutputName = outputName.trim()

    setNodesDirty((prevNodes) => 
      prevNodes.map((n) => {
        if (n.id !== node.id) return n;
        return {
          ...n,
          data: {
            ...n.data,
            output_name: finalOutputName ? finalOutputName : null,
            params: newParams
          }
        };
      })
    );
    setGlobalError(null);
  };

  if (selectedCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center text-[var(--text-secondary)]">
        <div className="w-12 h-12 mb-4 rounded-full bg-[var(--bg-panel-hover)] flex items-center justify-center">
          <List size={24} className="opacity-50" />
        </div>
        <p className="text-sm">Select a node to edit its parameters</p>
      </div>
    );
  }

  if (selectedCount > 1) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center text-[var(--text-secondary)]">
        <div className="w-12 h-12 mb-4 rounded-full bg-[var(--accent-blue-dim)] flex items-center justify-center text-[var(--accent-blue)]">
          <List size={24} />
        </div>
        <p className="text-sm font-medium text-[var(--text-primary)]">{selectedCount} nodes selected</p>
        <p className="text-xs mt-1">Select a single node to edit parameters</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-[var(--border-subtle)] bg-[var(--bg-panel)]">
        <div className="flex items-center gap-2 mb-1">
             <h3 className="font-semibold text-sm">{node?.data?.label || nodeType}</h3>
             {def?.category && <span className="badge badge-blue">{def.category}</span>}
        </div>
        <p className="text-xs text-[var(--text-secondary)] line-clamp-2">{def?.description || node?.data?.subtitle || 'Configure parameters'}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">

         <div className="flex flex-col gap-1">
           <label className="text-xs text-[var(--text-secondary)]">Output Name</label>
           <input
             className="input text-xs h-8 px-2 font-mono bg-[var(--bg-input)]"
             placeholder="e.g. rsi_val"
             value={outputName}
             onChange={(e) => setOutputName(e.target.value)}
             spellCheck={false}
           />
           <div className="text-[10px] text-[var(--text-tertiary)]">
             Stored as node.data.output_name; used as variable name in DAG v12.
           </div>
         </div>
        
        {globalError && (
            <div className="flex items-center gap-2 text-xs text-[var(--accent-red)] p-2 bg-[var(--accent-red-dim)] rounded-md border border-[var(--accent-red)]/20">
                <AlertCircle size={14} />
                {globalError}
            </div>
        )}

        <div className="flex flex-col gap-2">
            {rows.map((row) => (
                <div key={row.id} className="flex flex-col gap-1 p-2 rounded-md bg-[var(--bg-panel-hover)] border border-[var(--border-subtle)] group">
                    <div className="flex items-start gap-2">
                        <div className="flex flex-col flex-1 min-w-[120px]">
                            <input 
                                className="input text-xs h-8 px-2 font-mono bg-[var(--bg-input)]"
                                placeholder="Key"
                                value={row.key}
                                onChange={(e) => handleRowChange(row.id, 'key', e.target.value)}
                            />
                        </div>

                        <div className="w-[90px]">
                             <select 
                                className="input text-xs h-8 px-1 appearance-none w-full bg-[var(--bg-input)] cursor-pointer"
                                value={row.type}
                                onChange={(e) => handleRowChange(row.id, 'type', e.target.value)}
                             >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Bool</option>
                                <option value="json">JSON</option>
                             </select>
                        </div>

                         <button 
                            onClick={() => handleRemoveRow(row.id)}
                            className="h-8 w-8 flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--accent-red)] hover:bg-[var(--accent-red-dim)] rounded transition-colors"
                            title="Remove parameter"
                         >
                            <Trash2 size={14} />
                        </button>
                    </div>

                    <div className="w-full">
                        {row.type === 'boolean' ? (
                            <button 
                                onClick={() => handleRowChange(row.id, 'value', !row.value)}
                                className={`flex items-center justify-center gap-2 w-full h-8 rounded text-xs transition-all border ${
                                    row.value 
                                    ? 'bg-[var(--accent-blue-dim)] border-[var(--accent-blue)] text-[var(--accent-blue)]' 
                                    : 'bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-secondary)]'
                                }`}
                            >
                                {row.value ? 'True' : 'False'}
                            </button>
                        ) : row.type === 'json' ? (
                            <div className="relative">
                                <textarea 
                                    className={`input text-xs font-mono w-full p-2 min-h-[60px] resize-y bg-[var(--bg-input)] ${row.error ? 'border-[var(--accent-red)]' : ''}`}
                                    placeholder="{}"
                                    value={row.value}
                                    onChange={(e) => handleRowChange(row.id, 'value', e.target.value)}
                                    spellCheck={false}
                                />
                                {row.error && <div className="text-[10px] text-[var(--accent-red)] mt-1">{row.error}</div>}
                            </div>
                        ) : (
                            <input 
                                type={row.type === 'number' ? 'number' : 'text'}
                                className="input text-xs h-8 px-2 w-full bg-[var(--bg-input)]"
                                placeholder="Value"
                                value={row.value}
                                onChange={(e) => handleRowChange(row.id, 'value', e.target.value)}
                                step={row.type === 'number' ? "any" : undefined}
                            />
                        )}
                    </div>
                </div>
            ))}

            {rows.length === 0 && (
                <div className="text-center py-6 text-[var(--text-tertiary)] text-xs border border-dashed border-[var(--border-subtle)] rounded-md">
                    No parameters defined
                </div>
            )}

            <button 
                onClick={handleAddRow}
                className="btn w-full justify-center py-1.5 text-xs border-dashed border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--text-secondary)]"
            >
                <Plus size={14} />
                Add Parameter
            </button>
        </div>

      </div>

      <div className="p-4 border-t border-[var(--border-subtle)] bg-[var(--bg-panel)] flex gap-2">
         <button 
            className="btn btn-ghost flex-1 justify-center"
            onClick={handleReset}
            title="Reset changes"
        >
            <RotateCcw size={14} />
        </button>
        <button 
            className="btn btn-primary flex-[3] justify-center"
            onClick={handleApply}
        >
            <Check size={14} />
            Apply Changes
        </button>
      </div>
    </div>
  );
}
